/** Estimates the steady background-noise level of a recording: the average power of its quietest 10% of
 * 50 ms windows, in dBFS. Used to set the denoiser relative to this file instead of a fixed number. */
export function measureNoiseFloorDb(channels: Float32Array[], sampleRate: number): number {
  const length = channels[0]?.length ?? 0;
  const win = Math.max(1, Math.round(sampleRate * 0.05));
  const powers: number[] = [];
  for (let from = 0; from < length; from += win) {
    const to = Math.min(length, from + win);
    let sum = 0;
    for (const ch of channels) for (let i = from; i < to; i++) sum += ch[i] * ch[i];
    const p = sum / ((to - from) * channels.length);
    if (p > 1e-9) powers.push(p); // ignore digital silence (below -90 dB)
  }
  if (powers.length === 0) return -80;
  powers.sort((a, b) => a - b);
  const n = Math.max(1, Math.floor(powers.length * 0.25));
  const mean = powers.slice(0, n).reduce((a, b) => a + b, 0) / n;
  return 10 * Math.log10(mean);
}

/** afftdn's nf must be -80..-20, and it only removes noise that sits at or below the level it is told, so it is
 * set a few dB above the measured floor; the preset decides how far above. */
export function noiseFilterNf(floorDb: number, offsetDb: number): number {
  return Math.round(Math.max(-80, Math.min(-20, floorDb + offsetDb)));
}

/** How much the steady noise dropped: takes the quietest 10% of 100 ms windows of the ORIGINAL (the pauses),
 * then compares the power in those same stretches before and after. Returns dB (positive = quieter). */
export function measureNoiseReductionDb(before: Float32Array[], after: Float32Array[], sampleRate: number): number {
  const length = Math.min(before[0]?.length ?? 0, after[0]?.length ?? 0);
  const win = Math.max(1, Math.round(sampleRate * 0.1));
  const power = (chs: Float32Array[], from: number, to: number) => {
    let sum = 0;
    for (const ch of chs) for (let i = from; i < to; i++) sum += ch[i] * ch[i];
    return sum / ((to - from) * chs.length);
  };
  const wins: { from: number; to: number; p: number }[] = [];
  for (let from = 0; from + win <= length; from += win) {
    const p = power(before, from, from + win);
    if (p > 1e-9) wins.push({ from, to: from + win, p });
  }
  if (wins.length === 0) return 0;
  wins.sort((a, b) => a.p - b.p);
  const quiet = wins.slice(0, Math.max(1, Math.floor(wins.length * 0.1)));
  const pb = quiet.reduce((a, w) => a + w.p, 0) / quiet.length;
  const pa = quiet.reduce((a, w) => a + power(after, w.from, w.to), 0) / quiet.length;
  return 10 * Math.log10(pb / Math.max(pa, 1e-12));
}
